import React, { memo } from "react";
import { BrowserRouter as Router, Route as ReactRoute, useParams } from "react-router-dom";
import Switch from "@components/Switch";
import Route from "@components/Route";
import Layout from "../PageLayout/type";
import { UserProvider } from "@features/User/context";
import { ContextProvider } from "@features/Wallets/context/context";
import { showLanding } from "@core/utils";

import Scroll from "../Scroll";
import PageLayout from "@features/Layout/PageLayout";
import MarketPage from "@features/Market/pages";

const SignInPage = React.lazy(() =>
  import("@features/Registration/pages/SignIn")
);
const SignUpPage = React.lazy(() =>
  import("@features/Registration/pages/SignUp")
);
const ForgotPasswordPage = React.lazy(() =>
  import("@features/Registration/pages/ForgotPassword")
);
const ResetPasswordPage = React.lazy(() =>
  import("@features/Registration/pages/ResetPassword")
);

const Intro = React.lazy(() => import("@features/Dashboard/Intro"));

const WalletsPage = React.lazy(() => import("@features/Wallets/pages"));
const OrdersPage = React.lazy(() => import("@features/Orders/pages"));
const MarketOrderPage = React.lazy(() => import("@features/MarketOrder/pages"));
const SettingsPage = React.lazy(() => import("@features/Settings/pages"));
const ShetabSuccess = React.lazy(() =>
  import("@features/Wallets/components/RialWallet/ShetabSuccess")
);
const ShetabFailure = React.lazy(() =>
  import("@features/Wallets/components/RialWallet/ShetabFailure")
);

const Wallet = React.lazy(() =>
  import("@features/Wallets/components/CoinWallet")
);
const ChangeMobile = React.lazy(() =>
  import("@features/Settings/components/ChangeMobile")
);
const TransactionHistory = React.lazy(() =>
  import("@features/TransactionHistory/pages")
);

const DestinationWalletListPage = React.lazy(() =>
  import("@features/Settings/components/DestinationWalletsListPage")
);

const Activating2FAScanningQrCode = React.lazy(() =>
  import("@features/Settings/components/Activating2FASacanningQRCode")
);
const Deactivate2FA = React.lazy(() =>
  import("@features/Settings/components/Deactivating2FA")
);
const ChangePasswordPage = React.lazy(() =>
  import("@features/Settings/components/ChangePassword")
);

const UserFavoriteMarket = React.lazy(() =>
  import("@features/UserFavoriteMarket/Intro")
);
const EnableWhitelist = React.lazy(() =>
  import(
    "@features/Settings/components/DestinationWalletList/EnableAndDisableWhiteList"
  )
);
const EnableWhitelistForEachWallet = React.lazy(() =>
  import(
    "@features/Settings/components/DestinationWalletsListPage/DestinationWalletsListPlaceholder/EnableAndDisableWhiteList"
  )
);
const KycPage1 = React.lazy(() => import("@features/Kyc/Kyc1/pages"));
const KycPage2 = React.lazy(() => import("@features/Kyc/Kyc2/pages"));
const AuthenticationLevels = React.lazy(() =>
  import("@features/Kyc/AuthenticationLevels/Intro")
);
const Announcements = React.lazy(() =>
  import("@features/Notifications/components/Announcements")
);
const NotificationsManagement = React.lazy(() =>
  import("@features/Settings/components/NotificationsManagement")
);
const AccountManagement = React.lazy(() =>
  import("@features/Settings/components/AccountManagement")
);
const ActivityHistory = React.lazy(() =>
  import("@features/Settings/components/ActivityHistory")
);
const DeviceManagement = React.lazy(() =>
  import("@features/Settings/components/DeviceManagement")
);
const ApiDocumentationPage = React.lazy(() =>
  import("@features/ApiDocumentation/pages")
);
const LandingPage = React.lazy(() => import("@features/Landing/pages"));

const homeRoute = showLanding()
  ? {
      path: "/",
      showHeader: false,
      showFooter: false,
      authorize: false,
      publicAccess: true,
      layoutType: Layout.FullWidth,
      component: LandingPage,
    }
  : {
      path: "/",
      showHeader: true,
      authorize: true,
      showFooter: true,
      showMenu: true,
      component: Intro,
    };

export const routes = [
  homeRoute,
  {
    path: "/login",
    showHeader: false,
    showFooter: false,
    authorize: false,
    component: SignInPage,
    layoutType: Layout.FullWidth,
  },
  {
    path: "/register",
    showHeader: false,
    showFooter: false,
    authorize: false,
    component: SignUpPage,
    layoutType: Layout.FullWidth,
  },
  {
    path: "/forgotPassword",
    showHeader: false,
    showFooter: false,
    authorize: false,
    component: ForgotPasswordPage,
    layoutType: Layout.FullWidth,
  },
  {
    path: "/resetPassword",
    showHeader: false,
    showFooter: false,
    authorize: false,
    component: ResetPasswordPage,
    layoutType: Layout.FullWidth,
  },
  {
    path: "/settings/security/changePassword",
    authorize: true,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
    component: ChangePasswordPage,
  },
  {
    path: "/dashboard/:page",
    showHeader: true,
    authorize: true,
    showFooter: true,
    showMenu: true,
    component: Intro,
  },
  {
    path: "/market/:currencyName",
    showHeader: true,
    authorize: true,
    showMenu: true,
    menuOver: true,
    paddingLayout: true,
    component: MarketPage,
  },
  {
    path: "/user/kyc/:step",
    showHeader: false,
    authorize: true,
    component: KycPage1,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
  },
  {
    path: "/user/kyc2/:step",
    showHeader: false,
    authorize: true,
    component: KycPage2,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
  },
  {
    path: "/wallet/:page",
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: WalletsPage,
  },

  // {
  //   path: `/wallets/:currencyName`,
  //   showHeader: true,
  //   authorize: true,
  //   showMenu: true,
  //   showFooter: true,
  //   component: Wallet,
  // },
  {
    path: `/wallets/:currencyName/:operation`,
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: Wallet,
  },
  {
    path: `/app/form/irtpay/success`,
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: ShetabSuccess,
  },
  {
    path: `/app/form/irtpay/failure`,
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: ShetabFailure,
  },
  // {
  //   path: `/withdraw success`,
  //   showHeader: true,
  //   authorize: true,
  //   showMenu: true,
  //   showFooter: true,
  //   component: Wallet
  // },
  // {
  //   path: `/withdraw failure`,
  //   showHeader: true,
  //   authorize: true,
  //   showMenu: true,
  //   showFooter: true,
  //   component: Wallet
  // },
  {
    path: `/authenticationLevels`,
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: AuthenticationLevels,
  },

  {
    path: "/marketOrder/:page",
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: MarketOrderPage,
  },

  {
    path: "/orders/:page",
    showHeader: true,
    authorize: true,
    showMenu: true,
    component: OrdersPage,
  },

  {
    path: `/marketOrder/:page/:currencyName`,
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: MarketOrderPage,
  },
  {
    path: "/settings/:page",
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: SettingsPage,
  },

  {
    path: "/settings/account/destination-wallets-management-page",
    showHeader: true,
    authorize: true,
    showMenu: true,
    showFooter: true,
    component: DestinationWalletListPage,
  },

  {
    path: "/settings/security/activating-tfa-scanning-qr-code",
    authorize: true,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
    component: Activating2FAScanningQrCode,
  },
  {
    path: "/setting/deactivate2FA",
    authorize: true,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
    component: Deactivate2FA,
  },
  {
    path: "/announcements/:page",
    showHeader: true,
    authorize: true,
    showFooter: true,
    component: Announcements,
    showMenu: true,
  },
  {
    path: "/notifications-management",
    showHeader: true,
    authorize: true,
    showFooter: true,
    component: NotificationsManagement,
    showMenu: true,
  },
  {
    path: "/accountManagement",
    showHeader: true,
    authorize: true,
    showFooter: true,
    component: AccountManagement,
    showMenu: true,
  },
  {
    path: "/activityHistory",
    showHeader: true,
    authorize: true,
    component: ActivityHistory,
    showMenu: true,
  },
  {
    path: "/deviceManagement",
    showHeader: true,
    authorize: true,
    component: DeviceManagement,
    showMenu: true,
  },
  {
    path: "/changeMobile",
    authorize: true,
    component: ChangeMobile,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
  },
  {
    path: "/transaction-history/:page",
    showHeader: true,
    showMenu: true,
    showFooter: true,
    authorize: true,
    component: TransactionHistory,
  },
  {
    path: "/trade-market",
    showHeader: true,
    showMenu: true,
    showFooter: true,
    authorize: true,
    component: UserFavoriteMarket,
  },
  {
    path: "/enableWhitelist",
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
    authorize: true,
    component: EnableWhitelist,
  },
  {
    path: "/enable-whitelist-for-each-wallet",
    authorize: true,
    layoutType: Layout.FullWidth,
    backgroundColorWhite: true,
    component: EnableWhitelistForEachWallet,
  },
];

function MainRouter() {
  return (
    <Router>
      <Switch>
        <ReactRoute path="/api-docs" exact>
          <ApiDocumentationPage />
        </ReactRoute>
        <UserProvider>
          <ContextProvider>
            <PageLayout>
              {routes.map(({ component: Component, ...rest }) => (
                <Route exact {...rest} key={rest.path}>
                  <Scroll>
                    <Component />
                  </Scroll>
                </Route>
              ))}
            </PageLayout>
          </ContextProvider>
        </UserProvider>
      </Switch>
    </Router>
  );
}

export default memo(MainRouter);
